import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'store';
import { verifyToken } from 'actions/authActions';
import LoadingIndicator from 'components/loadingIndicator';
import PermissionWarning from 'components/common/permissionWarning';
import { useHasPermission } from 'components/permissions/hooks';
import { firstLoginUrl, loginUrl } from '../../routes';

interface ProtectedRouteProps {
    element: React.ReactNode;
    permission?: string | string[] | false;
}

export const ProtectedRoute: React.FC<React.PropsWithChildren<ProtectedRouteProps>> = ({ element: Element, permission }) => {
    const [loaded, setLoaded] = useState(false);
    
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { routePrefix } = useParams();

    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
    const isTermsOfUseAccepted = useAppSelector((state) => state.auth.isTermsOfUseAccepted);
    const compelPasswordChange = useAppSelector((state) => state.auth.compelPasswordChange);

    const hasPermission = useHasPermission(permission === false ? '' : permission ?? '') || permission === false;

    useEffect(() => {
        let unmounted = false;
        dispatch(verifyToken()).then(() => {
            if (!unmounted) setLoaded(true);
        });

        return () => {
            unmounted = true;
        };
    }, [dispatch]);

    useEffect(() => {
        if (loaded) {
            if (!isAuthenticated && !location.pathname.includes("/login")) {
                navigate(loginUrl(routePrefix), { state: { from: location } });
                return;
            }

            if (isAuthenticated && (!isTermsOfUseAccepted || compelPasswordChange) && !location.pathname.includes("/user/firstlogin")) {
                navigate(firstLoginUrl(routePrefix), { state: { from: location } });
                return;
            }
        }
    }, [loaded, isAuthenticated, isTermsOfUseAccepted, compelPasswordChange, location, navigate, routePrefix]);

    if (!loaded) {
        return <LoadingIndicator />;
    }

    if (!hasPermission) {
        return <PermissionWarning />;
    }

    return <>{Element}</>;
};

export default ProtectedRoute;
